// Presentational chart primitives for the Dashboard: KPI cards, horizontal bar
// lists, age-stacked bars, and paired weekly columns. Hand-rolled divs styled
// with Fluent tokens — the shapes are simple enough that a chart library would
// not earn its place (and zero deps keeps the app portable).
import type { ReactNode } from 'react'
import { Badge, Subtitle2, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'

const useStyles = makeStyles({
  panel: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '16px 20px',
    minWidth: 0,
  },
  panelHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '12px',
  },
  panelHint: { color: tokens.colorNeutralForeground3 },
  kpi: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: '14px 16px',
    minWidth: 0,
  },
  kpiLabel: { color: tokens.colorNeutralForeground3, display: 'block', marginBottom: '4px' },
  kpiValue: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, lineHeight: '1.2' },
  kpiSub: { display: 'block', marginTop: '2px', color: tokens.colorNeutralForeground3 },
  kpiSubDanger: { color: tokens.colorPaletteRedForeground1 },
  barTable: { display: 'flex', flexDirection: 'column', gap: '6px' },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '132px minmax(0, 1fr) 64px',
    alignItems: 'center',
    columnGap: '8px',
  },
  barRowClickable: {
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusSmall,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  barLabel: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  barTrack: { display: 'flex', height: '14px', minWidth: 0 },
  barSeg: { height: '100%' },
  barValue: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  dimmed: { opacity: 0.35 },
  columns: { display: 'flex', alignItems: 'flex-end', gap: '6px', height: '96px' },
  columnPair: { flex: 1, display: 'flex', alignItems: 'flex-end', gap: '2px', minWidth: 0, height: '100%' },
  column: { flex: 1, borderRadius: '2px 2px 0 0', minHeight: '1px' },
  axis: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '6px',
    color: tokens.colorNeutralForeground3,
  },
  legendSwatch: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '2px',
    marginRight: '4px',
    verticalAlign: 'baseline',
  },
})

// ---- Panel + KPI -------------------------------------------------------------
export function PanelCard({ title, hint, badge, children }: {
  title: string
  hint?: ReactNode
  badge?: string
  children: ReactNode
}) {
  const s = useStyles()
  return (
    <div className={s.panel}>
      <div className={s.panelHead}>
        <Subtitle2>{title}</Subtitle2>
        {badge ? <Badge appearance="tint" color="informative" size="small">{badge}</Badge> : null}
        {hint ? <Caption1 className={s.panelHint}>{hint}</Caption1> : null}
      </div>
      {children}
    </div>
  )
}

export function KpiCard({ label, value, sub, subDanger }: {
  label: string
  value: string
  sub?: string
  subDanger?: boolean
}) {
  const s = useStyles()
  return (
    <div className={s.kpi}>
      <Caption1 className={s.kpiLabel}>{label}</Caption1>
      <span className={s.kpiValue}>{value}</span>
      {sub ? <Caption1 className={mergeClasses(s.kpiSub, subDanger && s.kpiSubDanger)}>{sub}</Caption1> : null}
    </div>
  )
}

export function LegendSwatch({ color, label }: { color: string; label: string }) {
  const s = useStyles()
  return (
    <span>
      <span className={s.legendSwatch} style={{ backgroundColor: color }} aria-hidden />
      {label}
    </span>
  )
}

// ---- Horizontal bars ----------------------------------------------------------
export interface BarSegment {
  color: string
  value: number
}

export interface BarRowData {
  key: string
  label: string
  /** Row magnitude — sets the bar length relative to the longest row. */
  total: number
  /** Right-aligned display of the total. */
  display: string
  /** Stacked segments; a single-color bar passes one segment. */
  segments: BarSegment[]
  title?: string
}

/** Label | bar | value rows. Clickable rows toggle a selection; when a
 * selection exists every other row dims (cross-filter affordance). */
export function HBarList({ rows, selectedKey, onSelect }: {
  rows: BarRowData[]
  selectedKey?: string | null
  onSelect?: (key: string) => void
}) {
  const s = useStyles()
  const max = Math.max(...rows.map((r) => r.total), 1)
  return (
    <div className={s.barTable}>
      {rows.map((r) => {
        const dim = !!selectedKey && selectedKey !== r.key
        const widthPct = Math.max((r.total / max) * 100, r.total > 0 ? 1.5 : 0)
        return (
          <div
            key={r.key}
            className={mergeClasses(s.barRow, onSelect && s.barRowClickable, dim && s.dimmed)}
            onClick={onSelect ? () => onSelect(r.key) : undefined}
            title={r.title ?? r.label}
            role={onSelect ? 'button' : undefined}
          >
            <span className={s.barLabel}>{r.label}</span>
            <div className={s.barTrack} style={{ width: `${widthPct}%`, borderRadius: '3px', overflow: 'hidden' }}>
              {r.segments.filter((seg) => seg.value > 0).map((seg, i) => (
                <div key={i} className={s.barSeg} style={{ flexGrow: seg.value, backgroundColor: seg.color }} />
              ))}
            </div>
            <span className={s.barValue}>{r.display}</span>
          </div>
        )
      })}
    </div>
  )
}

// ---- Paired weekly columns -----------------------------------------------------
export interface ColumnPair {
  key: string
  a: number
  b: number
  title: string
}

export function PairedColumns({ pairs, aColor, bColor, startLabel, endLabel }: {
  pairs: ColumnPair[]
  aColor: string
  bColor: string
  startLabel: string
  endLabel: string
}) {
  const s = useStyles()
  const max = Math.max(...pairs.map((p) => Math.max(p.a, p.b)), 1)
  const h = (v: number) => `${Math.max((v / max) * 100, v > 0 ? 2 : 0)}%`
  return (
    <div>
      <div className={s.columns}>
        {pairs.map((p) => (
          <div key={p.key} className={s.columnPair} title={p.title}>
            <div className={s.column} style={{ height: h(p.a), backgroundColor: aColor }} />
            <div className={s.column} style={{ height: h(p.b), backgroundColor: bColor }} />
          </div>
        ))}
      </div>
      <div className={s.axis}>
        <Caption1>{startLabel}</Caption1>
        <Caption1>{endLabel}</Caption1>
      </div>
    </div>
  )
}
